import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GasPump, Package, TrendUp, Info, Warning } from "@phosphor-icons/react";

const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

export default function PricingPanel({ tenant }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);
  const [bulk, setBulk] = useState({ percent: 3, scope: "plans" });
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);

  useEffect(() => {
    if (!tenant?.tenant_id) return;
    api.get(`/tenants/${tenant.tenant_id}/pricing`).then((r) => setP(r.data));
  }, [tenant?.tenant_id]);

  const save = async (patch) => {
    setBusy(true);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/pricing`, patch);
      setP(r.data);
      setSaved({ ok: true, msg: "Gespeichert" });
      setTimeout(() => setSaved(null), 2000);
    } catch (e) {
      setSaved({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    } finally { setBusy(false); }
  };

  const runBulk = async (dry) => {
    setBulkResult(null);
    setBulkPreview(null);
    try {
      const r = await api.post("/pricing/bulk-increase", { percent: parseFloat(bulk.percent), scope: bulk.scope, dry_run: dry });
      if (dry) setBulkPreview(r.data);
      else {
        setBulkResult(r.data);
        setBulkPreview(null);
      }
    } catch (e) {
      setSaved({ ok: false, msg: e.response?.data?.detail || "Fehler" });
    }
  };

  if (!p) return <div className="text-sm text-zinc-400 p-6">Lade Preis-Einstellungen…</div>;

  const hubExample = Math.round(p.hub_base_price_cents * (30 / p.hub_per_meters) * p.hub_multiplier);

  return (
    <div className="space-y-6" data-testid="pricing-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Preise & Zuschläge</h2>
          <p className="text-sm text-zinc-500 mt-1">Globale Preisregeln — greifen auf alle Rechnungen automatisch.</p>
        </div>
        {saved && <span className={`text-xs font-medium ${saved.ok ? "text-emerald-700" : "text-red-600"}`}>{saved.ok ? "✓" : "✗"} {saved.msg}</span>}
      </div>

      {/* Treibstoffzuschlag */}
      <section className="bg-white border border-black/10 p-5" data-testid="fuel-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <GasPump size={18} weight="bold" /> Treibstoffzuschlag
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Wird auf jede Rechnungsposition (nach Rabatt, vor HUB) aufgeschlagen. 0 % = kein Zuschlag.
        </p>
        <div className="flex items-end gap-3">
          <div className="w-40">
            <Label className="text-xs">Zuschlag (%)</Label>
            <Input
              type="number"
              step="0.1"
              min={0}
              max={100}
              className="h-10 rounded-none"
              value={p.fuel_surcharge_pct}
              onChange={(e) => setP({ ...p, fuel_surcharge_pct: parseFloat(e.target.value) || 0 })}
              onBlur={() => save({ fuel_surcharge_pct: p.fuel_surcharge_pct })}
              data-testid="fuel-pct-input"
            />
          </div>
          <div className="text-xs text-zinc-600 mb-2">
            Beispiel: 14,90 € Reinigung → Aufschlag {eur(Math.round(1490 * (p.fuel_surcharge_pct / 100)))}
          </div>
        </div>
      </section>

      {/* Umsatzsteuer */}
      <section className="bg-white border border-black/10 p-5" data-testid="vat-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          € Umsatzsteuer (Standard)
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Standard-USt-Satz für alle Rechnungen dieses Mandanten. Wird beim Kunden erneut überschrieben, wenn er als USt-befreit markiert ist (z. B. Kleinunternehmer nach § 19 UStG).
        </p>
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">USt-Satz (%)</Label>
            <Input
              type="number"
              step="0.1"
              min={0}
              max={100}
              className="h-10 rounded-none"
              value={p.vat_rate_percent ?? 19}
              onChange={(e) => setP({ ...p, vat_rate_percent: parseFloat(e.target.value) || 0 })}
              onBlur={() => save({ vat_rate_percent: p.vat_rate_percent })}
              data-testid="vat-rate-input"
            />
          </div>
          <div>
            <Label className="text-xs">Standard-Rechtsgrundlage (optional)</Label>
            <Input
              type="text"
              className="h-10 rounded-none"
              value={p.vat_default_article || ""}
              onChange={(e) => setP({ ...p, vat_default_article: e.target.value })}
              onBlur={() => save({ vat_default_article: p.vat_default_article })}
              placeholder="z. B. Regelbesteuerung"
              data-testid="vat-article-input"
            />
          </div>
          <div>
            <Label className="text-xs">Standard-Hinweis (optional)</Label>
            <Input
              type="text"
              className="h-10 rounded-none"
              value={p.vat_default_note || ""}
              onChange={(e) => setP({ ...p, vat_default_note: e.target.value })}
              onBlur={() => save({ vat_default_note: p.vat_default_note })}
              placeholder="z. B. inkl. gesetzlicher USt"
              data-testid="vat-note-input"
            />
          </div>
        </div>
        <div className="text-xs text-zinc-600 mt-3">
          Beispiel: 14,90 € netto × {(p.vat_rate_percent ?? 19)} % USt = <strong>{eur(Math.round(1490 * ((p.vat_rate_percent ?? 19) / 100)))}</strong> Steuer, Brutto {eur(1490 + Math.round(1490 * ((p.vat_rate_percent ?? 19) / 100)))}
        </div>
      </section>

      {/* HUB */}
      <section className="bg-gradient-to-br from-amber-50 to-white border border-black/10 p-5" data-testid="hub-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <Package size={18} weight="bold" /> Hol- & Bringservice (HUB)
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Der Kunde bucht bei Wunsch einen HUB-Aufschlag: Distanz vom öffentlichen Standplatz zur Abholstelle.
          Der Fahrer bestätigt vor Ort (Versicherung). Preis = Grundpreis × (Meter / Referenzmeter) × Regionaler Multiplikator.
        </p>
        <div className="grid sm:grid-cols-4 gap-3">
          <label className="col-span-full flex items-center gap-2">
            <input
              type="checkbox"
              checked={p.hub_enabled}
              onChange={(e) => save({ hub_enabled: e.target.checked })}
              className="w-5 h-5"
              data-testid="hub-enabled-checkbox"
            />
            <span className="text-sm font-medium">HUB-Service anbieten</span>
          </label>
          <div>
            <Label className="text-xs">Grundpreis (€)</Label>
            <Input
              type="number"
              step="0.01"
              className="h-10 rounded-none"
              value={(p.hub_base_price_cents / 100).toFixed(2)}
              onChange={(e) => setP({ ...p, hub_base_price_cents: Math.round(parseFloat(e.target.value) * 100) })}
              onBlur={() => save({ hub_base_price_cents: p.hub_base_price_cents })}
              disabled={!p.hub_enabled}
              data-testid="hub-base-input"
            />
          </div>
          <div>
            <Label className="text-xs">Pro X Meter</Label>
            <Input
              type="number"
              min={1}
              className="h-10 rounded-none"
              value={p.hub_per_meters}
              onChange={(e) => setP({ ...p, hub_per_meters: parseInt(e.target.value) || 10 })}
              onBlur={() => save({ hub_per_meters: p.hub_per_meters })}
              disabled={!p.hub_enabled}
              data-testid="hub-per-meters-input"
            />
          </div>
          <div>
            <Label className="text-xs">Regionaler Multiplikator</Label>
            <Input
              type="number"
              step="0.05"
              min={0.1}
              className="h-10 rounded-none"
              value={p.hub_multiplier}
              onChange={(e) => setP({ ...p, hub_multiplier: parseFloat(e.target.value) || 1.0 })}
              onBlur={() => save({ hub_multiplier: p.hub_multiplier })}
              disabled={!p.hub_enabled}
              data-testid="hub-multiplier-input"
            />
          </div>
        </div>
        <div className="mt-4 flex items-start gap-2 p-3 rounded bg-white border border-amber-200 text-xs text-zinc-700">
          <Info size={14} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <strong>Beispiel</strong>: Standardstartwert 1,49 € pro 10 m mit Multiplikator 1,0 → 30 m HUB = {eur(hubExample)}. Beim aktuellen Setting kostet 30 m HUB <strong>{eur(hubExample)}</strong>.
          </div>
        </div>
      </section>

      {/* Preiserhöhung en masse */}
      <section className="bg-white border border-black/10 p-5" data-testid="bulk-increase-panel">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
          <TrendUp size={18} weight="bold" /> Preiserhöhung (Massenaktion)
        </h3>
        <p className="text-xs text-zinc-500 mb-4">
          Erhöht alle Plan-Preise um X %. Kunden mit „Preise beibehalten" (Kundenprofil) bleiben unverändert.
          Prüfen Sie zuerst per <em>Vorschau</em>, bevor Sie tatsächlich anwenden.
        </p>
        <div className="grid sm:grid-cols-[100px_1fr_auto_auto] gap-3 items-end">
          <div>
            <Label className="text-xs">Erhöhung (%)</Label>
            <Input
              type="number"
              step="0.1"
              min={0.01}
              max={100}
              className="h-10 rounded-none"
              value={bulk.percent}
              onChange={(e) => setBulk({ ...bulk, percent: e.target.value })}
              data-testid="bulk-percent-input"
            />
          </div>
          <div>
            <Label className="text-xs">Anwenden auf</Label>
            <select
              value={bulk.scope}
              onChange={(e) => setBulk({ ...bulk, scope: e.target.value })}
              className="h-10 w-full border border-black/10 rounded-none px-2 text-sm bg-white"
              data-testid="bulk-scope-select"
            >
              <option value="plans">Nur Plan-Preise (Standard-Preisliste)</option>
              <option value="customer_overrides">Nur Kunden-Sonderpreise</option>
              <option value="both">Beides (Pläne + Kunden-Sonderpreise)</option>
            </select>
          </div>
          <Button onClick={() => runBulk(true)} variant="outline" className="rounded-none h-10" data-testid="bulk-preview-btn">
            Vorschau
          </Button>
          <Button
            onClick={() => {
              if (!bulkPreview) { setSaved({ ok: false, msg: "Bitte zuerst Vorschau prüfen" }); return; }
              if (window.confirm(`${bulk.percent} % Preiserhöhung wird jetzt angewendet. Fortfahren?`)) runBulk(false);
            }}
            className="rounded-none bg-black text-white hover:bg-zinc-800 h-10"
            data-testid="bulk-apply-btn"
          >
            Anwenden
          </Button>
        </div>

        {bulkPreview && (
          <div className="mt-4 p-4 rounded bg-blue-50 border border-blue-200 text-sm space-y-1" data-testid="bulk-preview">
            <div className="font-bold flex items-center gap-2">
              <Info size={14} weight="fill" className="text-blue-600" /> Vorschau (nicht gespeichert)
            </div>
            <div>Betroffene Pläne: <strong>{bulkPreview.plans_touched}</strong></div>
            <div>Betroffene Kunden-Sonderpreise: <strong>{bulkPreview.customer_overrides_touched}</strong></div>
            <div>Übersprungen („Preise beibehalten"): <strong>{bulkPreview.customers_frozen}</strong></div>
          </div>
        )}
        {bulkResult && (
          <div className="mt-4 p-4 rounded bg-emerald-50 border border-emerald-200 text-sm space-y-1" data-testid="bulk-result">
            <div className="font-bold flex items-center gap-2">✓ Angewendet</div>
            <div>Pläne aktualisiert: <strong>{bulkResult.plans_touched}</strong></div>
            <div>Kunden-Sonderpreise: <strong>{bulkResult.customer_overrides_touched}</strong></div>
            <div>Kunden mit Preisstopp übersprungen: <strong>{bulkResult.customers_frozen}</strong></div>
          </div>
        )}
      </section>

      <div className="text-xs text-zinc-500 flex items-start gap-2 p-3 rounded bg-zinc-50 border border-black/5">
        <Warning size={14} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>Individuelle Kunden-Sonderpreise, Rabatte und HUB-Buchung findest du direkt im jeweiligen Kunden-Datensatz (Tab „Kunden" → Kunde öffnen).</div>
      </div>
    </div>
  );
}
